import { createContext, useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react'

import type { ArcgisDashboardCrawl } from '../../../services/api/domains/arcgisDashboards.api'
import { createDashboardBridge, type DashboardBridge, type RobotChatContext } from './DashboardBridge'

export const DashboardBridgeContext = createContext<DashboardBridge | null>(null)

export interface ArcgisDashboardRegistryContextValue {
  registerArcgisDashboardContext(elementId: string, crawl: ArcgisDashboardCrawl): void
  unregisterArcgisDashboardContext(elementId: string): void
}

export const ArcgisDashboardRegistryContext = createContext<ArcgisDashboardRegistryContextValue | null>(null)

export interface RobotRegistryContextValue {
  /**
   * A GETTER, not a value — the one shape difference from the ArcGIS registry
   * above, and it is not stylistic.
   *
   * A robot's context changes at telemetry rate (battery, posture, e-stop). Were
   * this a value, every element holding a robot would have to re-register on
   * every frame just so the chatbot could read a fresh battery level once per
   * turn. Storing the getter means registration runs on mount and unmount only,
   * and the answer is assembled at REQUEST time — which is also the only moment
   * it needs to be true. Same reason `DashboardMap` answers `cctv-cameras` out
   * of a ref rather than re-registering a handler per change.
   */
  registerRobotContext(robotId: string, getRobot: () => RobotChatContext): void
  unregisterRobotContext(robotId: string): void
}

export const RobotRegistryContext = createContext<RobotRegistryContextValue | null>(null)

/**
 * One bridge instance per dashboard, plus the per-dashboard ArcGIS-dashboard
 * crawl registry. A dashboard can host multiple `arcgis_dashboard` elements;
 * `DashboardBridge.onRequest` now supports multiple handlers per kind (Task
 * 12), but this registry workaround is kept anyway — every element still
 * registers its crawl into this shared `Map` keyed by elementId, and ONE
 * handler here answers with the full list, rather than each element
 * registering its own `'arcgis-dashboard-context'` handler and racing the
 * others for a single reply. Collapsing this onto the plain multi-handler
 * path is Phase 2a ActionRegistry scope, not this task's.
 */
export function DashboardBridgeProvider({ children }: { children: ReactNode }) {
  const bridge = useMemo(() => createDashboardBridge(), [])
  const registryRef = useRef<Map<string, ArcgisDashboardCrawl>>(new Map())
  /*
   * Keyed by ROBOT id, not by element id — deliberately, and it is the one place
   * this registry differs from the ArcGIS one above.
   *
   * Two tiles can show the same robot (a teleop pane and a feed pane), and an
   * element-keyed map would then hand the chatbot the same robot twice. The
   * model would see "Rex" and "Rex", ask which one, and there is no answer to
   * that question because there is only one robot. Robot-keyed, the second
   * element's registration is simply the same robot re-stating itself.
   */
  const robotsRef = useRef<Map<string, () => RobotChatContext>>(new Map())

  useEffect(() => {
    return bridge.onRequest('arcgis-dashboard-context', () => [...registryRef.current.values()])
  }, [bridge])

  useEffect(() => {
    return bridge.onRequest('robots', () => [...robotsRef.current.values()].map(get => get()))
  }, [bridge])

  const registerArcgisDashboardContext = useCallback((elementId: string, crawl: ArcgisDashboardCrawl) => {
    registryRef.current.set(elementId, crawl)
  }, [])

  const unregisterArcgisDashboardContext = useCallback((elementId: string) => {
    registryRef.current.delete(elementId)
  }, [])

  const registerRobotContext = useCallback((robotId: string, getRobot: () => RobotChatContext) => {
    robotsRef.current.set(robotId, getRobot)
  }, [])

  const unregisterRobotContext = useCallback((robotId: string) => {
    robotsRef.current.delete(robotId)
  }, [])

  const robotRegistryValue = useMemo<RobotRegistryContextValue>(
    () => ({ registerRobotContext, unregisterRobotContext }),
    [registerRobotContext, unregisterRobotContext],
  )

  const registryValue = useMemo<ArcgisDashboardRegistryContextValue>(
    () => ({ registerArcgisDashboardContext, unregisterArcgisDashboardContext }),
    [registerArcgisDashboardContext, unregisterArcgisDashboardContext],
  )

  return (
    <DashboardBridgeContext.Provider value={bridge}>
      <ArcgisDashboardRegistryContext.Provider value={registryValue}>
        <RobotRegistryContext.Provider value={robotRegistryValue}>{children}</RobotRegistryContext.Provider>
      </ArcgisDashboardRegistryContext.Provider>
    </DashboardBridgeContext.Provider>
  )
}
